import React from 'react';
import { ForeignActivitiesBenefitTypeItemModel } from 'forms/sections/foreign/activities/benefits/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonScrollIntoView
} from 'types/props';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import DateControl from 'components/Form/DateControl';
import Country from 'components/Form/Country';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import { ErrorCode } from '@redux/modules/application/reducers/error';

interface Props extends Partial<ForeignActivitiesBenefitTypeItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
}

export default class OneTimeBenefit extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  };

  update = (queue: Partial<ForeignActivitiesBenefitTypeItemModel>) => {
    this.props.onUpdate({
      Received: this.props.Received,
      Country: this.props.Country,
      Value: this.props.Value,
      ValueEstimated: this.props.ValueEstimated,
      Reason: this.props.Reason,
      Obligated: this.props.Obligated,
      ObligatedExplanation: this.props.ObligatedExplanation,
      CountryFocused: this.props.CountryFocused,
      ...queue,
    });
  };

  updateReceived = (values: ForeignActivitiesBenefitTypeItemModel['Received']) => {
    this.update({
      Received: values,
    });
  };

  updateCountry = (values: ForeignActivitiesBenefitTypeItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateValue = (values: ForeignActivitiesBenefitTypeItemModel['Value']) => {
    this.update({
      Value: values,
    });
  };

  updateValueEstimated = (values: ForeignActivitiesBenefitTypeItemModel['ValueEstimated']) => {
    this.update({
      ValueEstimated: values,
    });
  };

  updateReason = (values: ForeignActivitiesBenefitTypeItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateObligated = (values: ForeignActivitiesBenefitTypeItemModel['Obligated']) => {
    this.update({
      Obligated: values,
    });
  };

  updateObligatedExplanation = (
    values: ForeignActivitiesBenefitTypeItemModel['ObligatedExplanation']
  ) => {
    this.update({
      ObligatedExplanation: values,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  };

  render() {
    return (
      <div className="onetime-benefit">
        <Field
          title={i18n.t('foreign.activities.benefit.oneTime.heading.received')}
          help="foreign.activities.benefit.oneTime.help.received"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${this.props.attribute}.Received`}
        >
          <DateControl
            name="Received"
            className="received"
            {...this.props.Received}
            minDateEqualTo
            onUpdate={this.updateReceived}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${this.props.attribute}.Received`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.benefit.oneTime.heading.country')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${this.props.attribute}.Country`}
        >
          <Country
            name="Country"
            {...this.props.Country}
            excludeUnitedStates
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${this.props.attribute}.Country`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.benefit.oneTime.heading.value')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${this.props.attribute}.Value`}
        >
          <Currency
            name="Value"
            className="value"
            {...this.props.Value}
            min="0"
            onUpdate={this.updateValue}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${this.props.attribute}.Value`}
          />
          <div className="flags">
            <Checkbox
              name="ValueEstimated"
              label={i18n.t('foreign.activities.benefit.oneTime.label.valueEstimated')}
              toggle="false"
              {...this.props.ValueEstimated}
              onUpdate={this.updateValueEstimated}
              onError={this.props.onError}
              attribute={`${this.props.attribute}.ValueEstimated`}
            />
          </div>
        </Field>

        <Field
          title={i18n.t('foreign.activities.benefit.oneTime.heading.reason')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${this.props.attribute}.Reason`}
        >
          <Textarea
            name="Reason"
            className="reason"
            {...this.props.Reason}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${this.props.attribute}.Reason`}
          />
        </Field>

        <Branch
          name="Obligated"
          className="obligated no-margin-bottom"
          label={i18n.t('foreign.activities.benefit.oneTime.heading.obligated')}
          labelSize="h4"
          {...this.props.Obligated}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateObligated}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${this.props.attribute}.Obligated`}
        />

        <Show when={(this.props.Obligated || {}).value === 'Yes'}>
          <Field
            title={i18n.t('foreign.activities.benefit.oneTime.label.obligatedExplanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${this.props.attribute}.ObligatedExplanation`}
          >
            <Textarea
              name="Explanation"
              className="explanation"
              {...this.props.ObligatedExplanation}
              onUpdate={this.updateObligatedExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${this.props.attribute}.ObligatedExplanation`}
            />
          </Field>
        </Show>
      </div>
    );
  }
}

OneTimeBenefit.defaultProps = {
  Obligated: {},
  onUpdate: (queue) => { },
  CountryFocused: false,
  onError: (value, arr: ErrorCode) => arr,
};
